import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Boxes, CheckCircle2, Plus, TrendingDown } from 'lucide-react';
import type { StockCriticoVista } from '@shared/types';
import { CargaRapidaModal } from '@renderer/components/inventario/CargaRapidaModal';

/**
 * Sprint 9.3 — Componente único, accionable y predictivo.
 *
 * Reemplaza `StockCriticoWidget` (huérfano) y el bloque inline duplicado
 * en el Dashboard. Mejoras vs versión informativa anterior:
 *
 *   1) Proyección humana: "agotado en ~2h" cuando el backend lo calcula.
 *   2) Acción inline: botón "Cargar rápido" abre `<CargaRapidaModal />`
 *      sin saltar a /inventario.
 *   3) Mensaje vacío celebrativo (no se queda en blanco).
 */

const REFRESCO_MS = 30_000;

/**
 * @param ocultarSiVacio Sprint 31 — si true, no renderiza nada cuando no hay
 *   insumos críticos (el Dashboard muestra un chip ✓ en su lugar). Si false
 *   (default), muestra la card celebrativa "Existencias en orden".
 */
export function StockCriticoCard({
  ocultarSiVacio = false,
}: { ocultarSiVacio?: boolean } = {}) {
  const [criticos, setCriticos] = useState<StockCriticoVista[]>([]);
  const [cargando, setCargando] = useState(true);
  const [cargaRapida, setCargaRapida] = useState<StockCriticoVista | null>(null);

  const cargar = async () => {
    const res = await window.api.insumos.stockCritico();
    if (res.ok) {
      // Filtrar solo los críticos reales (semáforo rojo o amarillo).
      setCriticos(res.data.filter((c) => c.semaforo !== 'verde'));
    }
    setCargando(false);
  };

  useEffect(() => {
    cargar();
    const t = window.setInterval(cargar, REFRESCO_MS);
    return () => window.clearInterval(t);
  }, []);

  if (cargando) return null;

  if (criticos.length === 0) {
    if (ocultarSiVacio) return null;
    return (
      <Link
        to="/inventario"
        className="card flex items-center gap-3 hover:border-primary hover:shadow-md cursor-pointer"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
          <CheckCircle2 className="h-6 w-6" />
        </div>
        <div>
          <div className="font-semibold">Existencias en orden</div>
          <div className="text-sm text-slate-500">
            Ningún insumo en stock crítico ahora mismo.
          </div>
        </div>
      </Link>
    );
  }

  const rojos = criticos.filter((c) => c.semaforo === 'rojo').length;
  return (
    <div className="card border-amber-300 bg-amber-50 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-200 text-amber-900">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div>
            <div className="font-semibold text-amber-900">Stock crítico</div>
            <div className="text-sm text-amber-800">
              {criticos.length} insumo{criticos.length > 1 ? 's' : ''} con poca existencia
              {rojos > 0 && ` · ${rojos} en rojo`}
            </div>
          </div>
        </div>
        <Link
          to="/inventario"
          className="text-xs font-medium text-amber-900 underline hover:text-amber-700"
        >
          Ver todo →
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {criticos.slice(0, 8).map((c) => (
          <StockCriticoCardItem
            key={c.insumoId}
            item={c}
            onCargar={() => setCargaRapida(c)}
          />
        ))}
      </div>
      {criticos.length > 8 && (
        <p className="text-xs text-amber-800 text-center">
          +{criticos.length - 8} insumo{criticos.length - 8 > 1 ? 's' : ''} más · <Link to="/inventario" className="underline">Ver todo</Link>
        </p>
      )}

      <CargaRapidaModal
        abierto={cargaRapida !== null}
        insumoId={cargaRapida?.insumoId ?? null}
        nombre={cargaRapida?.nombre ?? ''}
        unidad={cargaRapida?.unidad ?? ''}
        mermaCoccionPct={cargaRapida?.mermaCoccionPct ?? 0}
        onClose={() => setCargaRapida(null)}
        onGuardado={() => {
          setCargaRapida(null);
          cargar();
        }}
      />
    </div>
  );
}

function StockCriticoCardItem({
  item,
  onCargar,
}: {
  item: StockCriticoVista;
  onCargar: () => void;
}) {
  const esRojo = item.semaforo === 'rojo';
  const tono = esRojo
    ? 'bg-red-100 border-red-200 text-red-900'
    : 'bg-yellow-100 border-yellow-200 text-yellow-900';

  return (
    <div className={`flex flex-col gap-1.5 rounded-lg border px-3 py-2 ${tono}`}>
      {/* Nombre + icono */}
      <div className="flex items-start justify-between gap-1 min-w-0">
        <span className="font-semibold text-sm leading-tight line-clamp-2 min-w-0">
          {item.nombre}
        </span>
        {esRojo && <Boxes className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />}
      </div>

      {/* Stock actual + vendido */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
        <span className="font-mono font-semibold">
          {item.stockActual.toFixed(3)} {item.unidad}
        </span>
        {item.vendidoHoy !== undefined && item.vendidoHoy > 0 && (
          <span className="opacity-75 flex items-center gap-0.5">
            <TrendingDown className="h-3 w-3" />
            {item.vendidoHoy.toFixed(3)} hoy
          </span>
        )}
      </div>

      {/*
        Sprint 63 (Fase 3) — En qué se convierte lo que queda.
        "Quedan 2 kg de Picaña" no le dice a nadie cuántas órdenes puede prometer; en
        hora pico esa división no la hace ninguna persona, así que la alerta se ignora.
        Se muestran los dos que menos rinden: son los que se van a acabar primero.
      */}
      {item.rindeEn && item.rindeEn.length > 0 && (
        <div className="text-[11px] leading-snug opacity-90">
          Alcanza para{' '}
          {item.rindeEn.slice(0, 2).map((r, i) => (
            <span key={r.productoId}>
              {i > 0 ? ' o ' : ''}
              <strong>
                {r.unidades} {r.nombre}
              </strong>
            </span>
          ))}
        </div>
      )}

      {/* Barra de progreso */}
      <div className="flex items-center gap-1.5">
        <div className="h-1.5 flex-1 rounded-full bg-white/60 overflow-hidden">
          <div
            className={`h-1.5 rounded-full transition-all ${esRojo ? 'bg-red-500' : 'bg-yellow-500'}`}
            style={{ width: `${Math.min(100, Math.max(0, item.porcentaje))}%` }}
          />
        </div>
        <span className="text-[10px] font-mono opacity-75 w-6 text-right shrink-0">
          {Math.round(item.porcentaje)}%
        </span>
      </div>

      {/* Proyección + botón */}
      <div className="flex items-center justify-between gap-1 mt-0.5">
        <ProyeccionLabel horas={item.horasHastaAgotamiento ?? null} />
        <button
          onClick={onCargar}
          className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition flex-shrink-0"
          title="Registrar carga rápida"
        >
          <Plus className="h-3 w-3" />
          Cargar
        </button>
      </div>
    </div>
  );
}

function ProyeccionLabel({ horas }: { horas: number | null }) {
  if (horas === null) return null;
  if (horas === 0) {
    return (
      <span className="font-semibold uppercase tracking-wide">
        ⚠ Agotado
      </span>
    );
  }
  const texto = formatearHoras(horas);
  const urgente = horas < 2;
  return (
    <span className={urgente ? 'font-semibold' : 'opacity-80'}>
      Se agota en ~{texto}
    </span>
  );
}

function formatearHoras(h: number): string {
  if (h < 1) {
    const min = Math.max(1, Math.round(h * 60));
    return `${min} min`;
  }
  if (h < 24) {
    const horas = Math.round(h);
    return `${horas} h`;
  }
  const dias = Math.round(h / 24);
  return `${dias} día${dias > 1 ? 's' : ''}`;
}
